import { ensureScriptTag } from "@/utils/scriptHelper";
import { useCallback, useState, useEffect } from "react";
import {showFirstLayer } from '@s-group/react-usercentrics'
import Button from "../../components/Button";
import SettingIcon from "@/assets/icon/settings.svg";

declare global {
  interface Window {
    instgrm: any;
  }
}

type SocialMediaInstagramElementProps = {
  id: string,
  url: string,
  isConsentGiven: boolean,
  idConsentInitialized: boolean
};

function extractInstagramId(url: string) {
  const parts = url.split('/p/');
  if (parts.length < 2) return null;
  const remainder = parts[1];    
  const id = remainder.split('/')[0];
  return id;
}

export function SocialMediaInstagramElement(
  {
    id,
    url,
    isConsentGiven,
    idConsentInitialized
  }: SocialMediaInstagramElementProps) {
    const [width, setWidth] = useState(0);
    const [postHeight, setPostHeight] = useState(0);

    // Load Instagram's embed script
    useEffect(() => {
      if (isConsentGiven && idConsentInitialized) {
        if (!window.instgrm) {
          ensureScriptTag({
            src: '//www.instagram.com/embed.js',
            async: true,
            defer: true,
            crossOrigin: "anonymous",
            referrerPolicy: "no-referrer"
          });
        } else {
          window.instgrm.Embeds.process();
        }

        // Add a mutation observer to detect when Instagram has processed the embed
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && 
                mutation.target instanceof HTMLElement &&
                mutation.target.id?.startsWith('instagram-embed-') &&
                mutation.target.classList.contains('instagram-media-rendered')) {

              const height = mutation.target.getAttribute('height');
              if (height) {
                const heightNum = parseInt(height, 10);
                if (!isNaN(heightNum) && heightNum > 0) {
                  setPostHeight(heightNum);
                }
              }
            }
          });
        });

        // Start observing the container
        const container = document.getElementById(id);
        if (container) {
          observer.observe(container, { 
            childList: true, 
            subtree: true,
            attributes: true,
            attributeFilter: ['height']
          });
        }

        // Fallback: Poll for height changes
        const pollInterval = setInterval(() => {
          const iframe = document.querySelector('.instagram-media-rendered');
          if (iframe instanceof HTMLElement) {
            const height = iframe.getAttribute('height');
            if (height) {
              const heightNum = parseInt(height, 10);
              if (!isNaN(heightNum) && heightNum > 0) {
                setPostHeight(heightNum);
                clearInterval(pollInterval);
              }
            }
          }
        }, 100);

        return () => {
          observer.disconnect();
          clearInterval(pollInterval);
        };
      }
    }, [isConsentGiven, idConsentInitialized, id]);

    // Add a debug effect to log state changes
    useEffect(() => {
    }, [postHeight]);

    const ref = useCallback((element: HTMLDivElement) => {
      if(element) {
        const resizeObserver = new ResizeObserver(entries => {
          for (let entry of entries) {
            setWidth(entry.contentRect.width);
          }
        });

        if (element) {
          resizeObserver.observe(element);
        }

        return () => {
          if (element) {
            resizeObserver.unobserve(element);
          }
        };
      }
    }, []);


    const scaleFactor = width/325;

    return <>
      {isConsentGiven && idConsentInitialized &&
        <div 
          id={id} 
          ref={ref} 
          className="bg-gray-150 rounded-sm  min-h-24 overflow-hidden"
          style={{
            height: (width <= 325 && postHeight > 0) ? `${postHeight * scaleFactor}px` : undefined
          }}
        >
          <div
            className="origin-top-left bg-gray-50"
            style={{
              transform: (width < 325) ? `scale(${scaleFactor})` : undefined
            }}
          >
            <blockquote 
              className="instagram-media" 
              data-instgrm-permalink={`https://www.instagram.com/p/${extractInstagramId(url)}?utm_source=ig_embed&amp;utm_campaign=loading`}
              data-instgrm-version="14"
              style={{
                background: '#FFF',
                border: 0,
                borderRadius: '3px',
                boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)',
                margin: '1px',
                maxWidth: '540px',
                minWidth: '326px',
                padding: 0,
                width: 'calc(100% - 2px)'
              }}
            />
          </div>
        </div>
      }
      {!isConsentGiven && idConsentInitialized && 
        <div className="bg-gray-150 rounded-lg p-4 py-8 flex flex-col text-center">
          <div className="font-semibold">Externen Inhalt von Instagram freigeben</div>
          Wir benötigen deine Zustimmung, um externe Inhalte von Instagram anzuzeigen.
          <div>
            <Button className="w-auto inline" color="link-secondary" onClick={showFirstLayer}>
              <SettingIcon className="w-4 h-4 mr-blank" /> Einstellungen bearbeiten
            </Button>
          </div>
        </div>
      }
    </>
}

